import { useState, useEffect, useRef } from "react";
import { CheckCircle2, FileText, Play, RotateCcw, Sparkles, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/lib/markdown";
import { statusMeta } from "@/lib/topic-status";
import { useRoomChannel } from "@/hooks/useRoomChannel";
import {
  type Topic,
  type Template,
  type WorkProduct,
  fetchTopicTemplates,
  fetchWorkProducts,
} from "@/lib/api";
import { TemplateTab } from "./TemplateTab";
import { cn } from "@/lib/utils";

// ── Summary tab ──────────────────────────────────────────────────────────────

function SummaryTab({ topic }: { topic: Topic }) {
  return (
    <div className="space-y-4">
      {topic.summary_md ? (
        <>
          {topic.summary_is_provisional ? (
            <p className="inline-flex items-center gap-1 text-xs text-muted">
              <Sparkles className="size-3" />
              AI · provisional summary
            </p>
          ) : null}
          <Markdown source={topic.summary_md} />
        </>
      ) : (
        <p className="text-sm text-muted">
          No summary yet — it'll appear once this topic is discussed.
        </p>
      )}

      {topic.ai_suggested_resolution ? (
        <div className="flex items-start gap-3 rounded-lg border border-brand-600/20 bg-brand-600/5 px-4 py-3">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-brand-600" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
              AI suggests
            </p>
            <p className="text-sm text-text">{topic.ai_suggested_resolution}</p>
          </div>
          <Button size="sm" variant="outline" className="shrink-0">
            Accept
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ── Transcript tab ────────────────────────────────────────────────────────────

const CONN_DOT: Record<string, string> = {
  connecting: "bg-amber-500",
  open: "bg-emerald-500",
  closed: "bg-slate-400",
  error: "bg-red-500",
};
const CONN_LABEL: Record<string, string> = {
  connecting: "Connecting…",
  open: "Live",
  closed: "Disconnected",
  error: "Connection error",
};

function LiveTranscriptTab({ topic }: { topic: Topic }) {
  const channel = useRoomChannel(topic.slug);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Pin the transcript to the bottom as segments arrive.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [channel.segments.length, channel.interim]);

  return (
    <div className="space-y-3">
      {/* Connection status */}
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs text-muted">
          <span
            className={cn(
              "inline-flex h-2 w-2 rounded-full",
              CONN_DOT[channel.status] ?? "bg-slate-400",
              channel.status === "open" && "animate-pulse",
            )}
          />
          {CONN_LABEL[channel.status] ?? "Unknown"}
        </span>
        {(channel.status === "closed" || channel.status === "error") ? (
          <Button size="sm" variant="outline" onClick={channel.reconnect}>
            Reconnect
          </Button>
        ) : null}
      </div>

      {/* Transcript area */}
      <div className="h-72 overflow-y-auto rounded-md border border-border bg-bg/40 p-3 font-mono text-sm">
        {channel.segments.length === 0 && !channel.interim ? (
          <p className="py-10 text-center text-muted">
            {channel.status === "connecting"
              ? "Connecting…"
              : "No transcript yet — the discussion hasn't started."}
          </p>
        ) : (
          <div className="space-y-2">
            {channel.segments.map((s) => (
              <motion.div
                key={s.seq}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {s.speaker ? (
                  <span className="text-brand-600">{s.speaker}: </span>
                ) : null}
                <span className="text-text">{s.text}</span>
              </motion.div>
            ))}
            {channel.interim ? (
              <div className="italic text-muted">{channel.interim}</div>
            ) : null}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Template tab: imported from TemplateTab.tsx ───────────────────────────────

// ── Dialog ────────────────────────────────────────────────────────────────────

export function TopicDetailDialog({
  topic,
  open,
  onOpenChange,
}: {
  topic: Topic | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const status = topic ? statusMeta(topic.status) : null;
  const people = topic?.participants ?? [];

  const [activeTab, setActiveTab] = useState("summary");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [workProducts, setWorkProducts] = useState<WorkProduct[]>([]);

  // Fetch templates + work products when a topic is opened.
  useEffect(() => {
    if (!topic || !open) return;
    const ac = new AbortController();
    Promise.all([
      fetchTopicTemplates(topic.slug, ac.signal),
      fetchWorkProducts(topic.slug, ac.signal),
    ])
      .then(([tpls, wps]) => { setTemplates(tpls); setWorkProducts(wps); })
      .catch((e) => { if (e.name !== "AbortError") console.error("template fetch:", e); });
    return () => ac.abort();
  }, [topic?.slug, open]);

  function refreshWorkProducts() {
    if (!topic) return;
    fetchWorkProducts(topic.slug).then(setWorkProducts).catch(console.error);
  }

  // Reset when dialog closes.
  useEffect(() => {
    if (!open) {
      setTemplates([]);
      setWorkProducts([]);
      setActiveTab("summary");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        {topic ? (
          <>
            <DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
              <DialogTitle className="pr-8">{topic.title}</DialogTitle>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                {status ? (
                  <Badge variant={status.variant} className={status.className}>
                    {status.label}
                  </Badge>
                ) : null}
                {people.length ? (
                  <span className="inline-flex items-center gap-1">
                    <Users className="size-3" />
                    {people.length} in the room
                  </span>
                ) : null}
              </div>
            </DialogHeader>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList className="shrink-0 justify-start rounded-none border-b border-border bg-transparent px-6 py-0">
                <TabsTrigger
                  value="summary"
                  className="rounded-none border-b-2 border-transparent px-3 pb-3 pt-2 data-[state=active]:border-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Summary
                </TabsTrigger>
                <TabsTrigger
                  value="transcript"
                  className="rounded-none border-b-2 border-transparent px-3 pb-3 pt-2 data-[state=active]:border-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Transcript
                </TabsTrigger>
                {templates.map((tpl) => (
                  <TabsTrigger
                    key={tpl.id}
                    value={`tpl-${tpl.id}`}
                    className="inline-flex items-center gap-1.5 rounded-none border-b-2 border-transparent px-3 pb-3 pt-2 data-[state=active]:border-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    <FileText className="size-3" />
                    {tpl.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                <TabsContent value="summary" className="mt-0">
                  <SummaryTab topic={topic} />
                </TabsContent>

                <TabsContent value="transcript" className="mt-0">
                  {/* Only mount (and connect the WS) when this tab is active. */}
                  {activeTab === "transcript" ? (
                    <LiveTranscriptTab topic={topic} />
                  ) : null}
                </TabsContent>

                {templates.map((tpl) => (
                  <TabsContent key={tpl.id} value={`tpl-${tpl.id}`} className="mt-0">
                    {activeTab === `tpl-${tpl.id}` ? (
                      <TemplateTab
                        template={tpl}
                        workProduct={
                          workProducts.find((w) => w.template_id === tpl.id) ?? null
                        }
                        topicSlug={topic.slug}
                        onRenderComplete={refreshWorkProducts}
                      />
                    ) : null}
                  </TabsContent>
                ))}
              </div>
            </Tabs>

            {/* Footer — enter the room */}
            <div className="shrink-0 border-t border-border px-6 py-4">
              {topic.status === "active" ? (
                <Button
                  className="w-full"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/room/${topic.slug}`);
                  }}
                >
                  Join discussion
                </Button>
              ) : topic.status === "registered" ? (
                <Button
                  className="w-full"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/room/${topic.slug}`);
                  }}
                >
                  <Play />
                  Start now
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/room/${topic.slug}`);
                  }}
                >
                  <RotateCcw />
                  Resume
                </Button>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
